import React, { useContext, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Typography from '../../Typography';
import Button from '../../Button';
import { MuiTextField } from '../../MuiComponents/FormInputs';
import { AlertContext } from '../../SuccessOrFailedModal';
import { useValidateLogin } from '../../../hooks/auth';
import { useImage } from '../../../hooks/misc';
import { useUpdateProfile } from '../../../hooks/account';
import { getAvatarString } from '../../../actions/commonActions';
import {
  CUSTOMER_ADMIN,
  CUSTOMER_LEADER,
  customerRoles,
} from '../../../utils/constants/customerRoles';
import { siembaRoles } from '../../../utils/roles';
import {
  dataSpaceValidation,
  characterMaxLimit,
  nameValidation,
  required_message,
} from '../../../utils/validations';
import { customerMapping } from '../../../containers/pages/Admin/UserManagement/UsersList';

/**
 * ROAR-4802 (AC9) — the Profile pane, per Figma node 263-490.
 *
 * Built inside the ROAR-4899 branch although ROAR-4802 owns it, at the developer's request. It
 * slots into the ROAR-4941 registry exactly where `NotBuiltYet` sat, so nothing in the shell
 * changes — which is the extension point that registry was built for.
 *
 * Reads from `useValidateLogin` rather than a pane-local query; see `hooks/account.js`
 * (`useUpdateProfile`) for why, and for why success invalidates that app-wide key.
 *
 * **ROAR-4963 reconciled this against the endpoint that actually shipped** (ROAR-4964, under
 * ROAR-4802/P1.11). `PATCH /account/profile` accepts a STRICT allow-list of
 * `{first_name, last_name, email}` and rejects any other key on PRESENCE, all-or-nothing; `email`
 * is accepted but immutable. AC9 is the authority: a user edits their FULL NAME only.
 *
 * Three fields are read-only, and for different reasons:
 *  - ROLE, because AC3 ("Self-Role-Change Blocked") forbids a user changing their own role
 *    through ANY endpoint or form. It is rendered, never submitted.
 *  - EMAIL, because AC9 makes it read-only and the endpoint refuses to change it. It is NOT
 *    submitted: the allow-list tolerates the key, but a present email that disagrees with
 *    `users.email` is a 400, and the pane's copy comes from the `useValidateLogin` cache rather
 *    than from the user. An absent one is skipped outright (see `api/mutations/account.js`).
 *  - COMPANY NAME, for every role EXCEPT the leader tier (`customer_leader`, `customer_admin`).
 *    Renaming the organisation is a leader-level act, so a pentest manager, overseer or point of
 *    contact still reads the name without being able to change it.
 *
 * **ROAR-4899 re-enables the company name for the leader tier**, at the developer's explicit
 * direction and reversing that part of ROAR-4963. It carries a known, accepted risk: the gate as
 * SHIPPED rejects `company_name` on presence, all-or-nothing, and `company_name` lives on
 * `companies` rather than `users`, so the backend needs both a widened allow-list AND a write path
 * to the company row before a leader's rename can succeed. Until that lands, a leader's rename
 * 400s — which is exactly the failure ROAR-4963 removed.
 *
 * Two things keep the blast radius at that one save rather than at every save, which is what the
 * pre-ROAR-4963 code got wrong:
 *  - only a leader-tier role can dirty the field at all, and
 *  - the key is submitted only when it was ACTUALLY EDITED (`dirtyFields`), never echoed back
 *    unchanged. A leader renaming only themselves therefore sends the same two-key payload as
 *    everyone else and keeps working whatever the gate accepts.
 */

/**
 * Roles that may rename the company. `customer_admin` sits alongside `customer_leader` because
 * both are company-level administrators; the operational customer roles (pentest manager,
 * overseer, point of contact) and every Siemba-side role are not.
 */
const COMPANY_NAME_EDITORS = [CUSTOMER_LEADER, CUSTOMER_ADMIN];

/** Human label for a role id, across both role tables; falls back to the raw id. */
const roleTitle = (role) => customerMapping[role] || '-';

/**
 * Splits the single FULL NAME field the Figma shows back into the `first_name`/`last_name` columns
 * the `users` table actually has. First whitespace-delimited token is the first name, everything
 * after it is the last name, so "Maya van der Berg" keeps its surname intact. A single-token name
 * yields an empty last name rather than duplicating the first.
 */
export const splitFullName = (value = '') => {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  return { first_name: parts[0] || '', last_name: parts.slice(1).join(' ') };
};

/**
 * The Figma renders a non-editable field as a grey fill, but MUI's default disabled state only
 * dims the text — against this app's outlined inputs that reads as an ordinary editable field.
 * `WebkitTextFillColor` is the one that actually recolours disabled input text in WebKit; plain
 * `color` is ignored there.
 */
const DISABLED_FIELD_SX = {
  '& .MuiOutlinedInput-root.Mui-disabled': { backgroundColor: '#F4F6F8' },
  '& .MuiOutlinedInput-input.Mui-disabled': { WebkitTextFillColor: 'var(--grey1)' },
};

/**
 * react-hook-form v7 returns a `ref` meant for the raw <input>, but MUI's TextField forwards `ref`
 * to its ROOT <div>. Spreading `register()` straight onto MuiTextField therefore leaves RHF
 * holding a div: the field never registers, `reset()` cannot seed it and `isDirty` never flips.
 * Re-routing that one prop to `inputRef` is what makes the pair work.
 */
const bind = ({ ref, ...rest }) => ({ inputRef: ref, ...rest });

/**
 * Field label — small uppercase caption above the input, as the Figma renders it (not a MUI
 * floating label). A plain <label> rather than <Typography>, because Typography spreads every prop
 * onto its element and always renders a <p> for the caption variant, which cannot carry `htmlFor`.
 * The `caption` class is the same one Typography would have applied.
 */
const FieldLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className={'caption text-body uppercase tracking-wide font-semibold'}>
    {children}
  </label>
);

const SectionHeading = ({ title, subtitle, children }) => (
  <div className={'flex items-start justify-between gap-4'}>
    <div>
      <Typography className={'text-headings text-sm font-semibold'}>{title}</Typography>
      <Typography className={'text-body text-xs'}>{subtitle}</Typography>
    </div>
    {children}
  </div>
);

const Profile = () => {
  const { data } = useValidateLogin();
  const [popup] = useContext(AlertContext);
  const mutation = useUpdateProfile();

  const user = data?.user;
  console.log('-=- user', user);
  const profilePicture = useImage(user?.profile_picture, 'profile', true);

  /**
   * Leader-tier only, per ROAR-4899. Drives the field's disabled state AND gates the payload key
   * below — the second is the actual guarantee, the first is presentation, the same pairing the
   * ROLE field uses for AC3.
   *
   * BOTH role fields are checked, rather than `platform_role` short-circuiting to `role`. This
   * session payload carries the customer-tier role on `platform_role` (which is what the ROLE
   * input below renders) while ~every other call site in the app still reads `role`, and the two
   * do not always agree. Preferring one and falling back only when it is ABSENT silently locks
   * out anyone whose `platform_role` holds some other value while `role` says leader — and the
   * symptom is a disabled field with no explanation, not a visible denial. Either field naming a
   * leader-tier role is enough.
   */
  const canEditCompanyName =
    COMPANY_NAME_EDITORS.includes(user?.platform_role) || COMPANY_NAME_EDITORS.includes(user?.role);

  const defaults = useMemo(
    () => ({
      full_name: [user?.first_name, user?.last_name].filter(Boolean).join(' '),
      email: user?.email || '',
      company_name: user?.company_name || '',
    }),
    [user?.first_name, user?.last_name, user?.email, user?.company_name]
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, dirtyFields, isSubmitting },
  } = useForm({ defaultValues: defaults });

  // `useValidateLogin` may still be in flight on first paint, so the form mounts empty and is
  // seeded once the user lands. Resetting (rather than setValue-ing each field) also rebaselines
  // `isDirty`, which is what keeps SAVE CHANGES disabled until the user actually edits something.
  useEffect(() => {
    reset(defaults);
  }, [defaults, reset]);

  const onSubmit = async (values) => {
    const { first_name, last_name } = splitFullName(values.full_name);

    // Sent ONLY by a leader who actually changed it. Echoing an unchanged name back would put the
    // key on every one of their saves, which is how the pre-ROAR-4963 pane broke renaming
    // yourself too; and a non-leader can never reach this branch, so no engineer's save can
    // acquire the key even if the disabled attribute were bypassed in the DOM.
    const renamedCompany = canEditCompanyName && Boolean(dirtyFields.company_name);

    try {
      // EXACTLY the keys the endpoint is meant to write, and nothing else. A stray key is a 400
      // that discards the whole save, so nothing may be added here without a matching change to
      // `profileUpdateGate` in the backend — which `company_name` is still waiting on; see the
      // header for why that exposure is confined to a leader's rename.
      //
      // `last_name` is OMITTED when empty rather than sent blank. A mononym — "Cher" — splits to
      // an empty surname, and the gate validates any key that is PRESENT, rejecting a blank one
      // ("last_name cannot be blank"), while SKIPPING one that is absent. Sending it would make
      // every save by a single-named user fail; omitting it lets them through. The cost is that
      // an existing surname cannot be CLEARED here, which the endpoint cannot do anyway — blank
      // is a 400 by contract.
      //
      // `email` is NOT sent at all, though the allow-list tolerates it. The field is disabled, so
      // its value comes from the `useValidateLogin` cache rather than from the user — and the gate
      // 400s ("email cannot be changed") whenever a PRESENT email differs from `users.email`. Any
      // drift between that cached session and the row would therefore fail a save that only
      // touched the name. An ABSENT email is skipped by the gate's `hasOwnProperty` guard and can
      // never 400, so omitting it removes the failure mode instead of tolerating it.
      await mutation.mutateAsync({
        first_name,
        ...(last_name ? { last_name } : {}),
        // Not trimmed here: `dataSpaceValidation` already rejects a leading or trailing space in
        // the field, so padding never reaches this point and a trim would only hide the rule.
        ...(renamedCompany ? { company_name: values.company_name } : {}),
      });
      // Rebaseline against what was just saved, so the button falls back to disabled.
      reset(values);
      popup({ variant: 'success', title: 'Profile updated', okayText: 'Okay' });
    } catch (err) {
      popup({
        variant: 'error',
        title: 'Could not update profile',
        description: err?.response?.data?.message || 'Something went wrong. Please try again.',
        okayText: 'Okay',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <SectionHeading title={'Personal Details'} subtitle={'Your name and contact information.'}>
        {user?.profile_picture ? (
          <img
            src={profilePicture}
            alt={''}
            height={40}
            width={40}
            className={'h-10 w-10 shrink-0 rounded-full object-cover'}
          />
        ) : (
          <div
            className={
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-800'
            }
          >
            <span className={'text-[14px] font-semibold text-white'}>
              {getAvatarString(`${user?.first_name || ''} ${user?.last_name || ''}`)}
            </span>
          </div>
        )}
      </SectionHeading>

      <div className={'mt-4 grid grid-cols-1 gap-x-6 sm:grid-cols-2'}>
        <div>
          <FieldLabel htmlFor={'profile-full-name'}>Full Name</FieldLabel>
          <MuiTextField
            id={'profile-full-name'}
            margin={'dense'}
            size={'small'}
            className={'!text-sm'}
            error={Boolean(errors.full_name)}
            helperText={errors.full_name?.message || ''}
            {...bind(
              register('full_name', {
                required: required_message,
                ...nameValidation,
                // `required` only rejects an empty string and `nameValidation` allows spaces, so
                // "   " passes both and splits to an empty first_name — a 400 the user would see
                // as an unexplained popup. Caught here, against the field, instead.
                validate: (value) =>
                  splitFullName(value).first_name.length > 0 || 'Please enter your name',
              })
            )}
          />
        </div>
        <div>
          <FieldLabel htmlFor={'profile-email'}>Email Address</FieldLabel>
          {/*
            AC9: read-only. Disabled AND unvalidated — the value can only ever be the one seeded
            from `useValidateLogin`, so a format rule could never fire, and the endpoint refuses a
            changed address with a 400 regardless.
          */}
          <MuiTextField
            id={'profile-email'}
            margin={'dense'}
            size={'small'}
            disabled
            sx={DISABLED_FIELD_SX}
            {...bind(register('email'))}
          />
        </div>
      </div>

      <div className={'mt-2'}>
        <FieldLabel htmlFor={'profile-role'}>Role</FieldLabel>
        {/*
          AC3: a user may never change their own role. Disabled AND absent from the payload — the
          disabled attribute is presentation, the omission in onSubmit is the actual guarantee.
        */}
        <MuiTextField
          id={'profile-role'}
          margin={'dense'}
          size={'small'}
          disabled
          value={roleTitle(user?.platform_role)}
          sx={DISABLED_FIELD_SX}
        />
      </div>

      <hr className={'my-6 border-0 border-t border-[#0000001A]'} />

      <SectionHeading title={'Organization Details'} subtitle={'Manage your company profile.'}>
        <div
          className={'flex h-10 w-20 shrink-0 items-center justify-center rounded-md bg-[#E0F8F7]'}
        >
          <img
            src={'/images/OrganisationIcon.svg'}
            alt={''}
            aria-hidden={'true'}
            className={'h-[18px] w-[18px] shrink-0'}
          />
        </div>
      </SectionHeading>

      <div className={'mt-4'}>
        <FieldLabel htmlFor={'profile-company-name'}>Company Name</FieldLabel>
        {/*
          Editable for the leader tier only — see the header, including why the key reaches the
          payload only when it was actually edited. For every other role this stays registered so
          `reset(defaults)` still seeds it for display, and the rules below are omitted rather than
          merely unreachable: a disabled input is not submitted by RHF's `required` either way, but
          an unconditional rule would also fire on a seeded value the user cannot correct.

          `required` + `dataSpaceValidation` is the same pairing the admin-side rename uses
          (`components/Admin/Layout/EditCompanyModal`), so the two paths reject the same input. The
          255 ceiling is the backend column's, matching first_name/last_name.
        */}
        <MuiTextField
          id={'profile-company-name'}
          margin={'dense'}
          size={'small'}
          disabled={!canEditCompanyName}
          error={Boolean(errors.company_name)}
          helperText={errors.company_name?.message || ''}
          sx={canEditCompanyName ? undefined : DISABLED_FIELD_SX}
          {...bind(
            register(
              'company_name',
              canEditCompanyName
                ? {
                    required: required_message,
                    ...dataSpaceValidation,
                    maxLength: { value: 255, message: `Company name ${characterMaxLimit(255)}` },
                  }
                : {}
            )
          )}
        />
        <Typography variant={'caption'} className={'text-body'}>
          Shown on your reports and shared with your team.
        </Typography>
      </div>

      <div className={'mt-6'}>
        <Button type={'submit'} disabled={!isDirty || isSubmitting || mutation.isLoading}>
          SAVE CHANGES
        </Button>
      </div>
    </form>
  );
};

export default Profile;
